// src/utils/results/buildDailyMetricTrend.js

import { formatDayMonth, getLocalDayKeyMs } from "./recordingIdTime";

const EMPTY_DAILY_TREND = {
  labels: [],
  xValues: [],
  values: [],
  minMax: [],
  counts: [],
};

export function buildDailyMetricTrend({
  rows,
  language,
  getValue,
  getTimestamp = (row) => row?.timestamp,
}) {
  if (!Array.isArray(rows) || !rows.length || typeof getValue !== "function") {
    return EMPTY_DAILY_TREND;
  }

  const chron = [...rows].reverse();
  const byDay = new Map();

  for (const row of chron) {
    const timestamp = getTimestamp(row);
    const ms = timestamp ? Date.parse(timestamp) : NaN;

    if (!Number.isFinite(ms)) continue;

    const value = getValue(row);

    if (typeof value !== "number" || !Number.isFinite(value)) continue;

    const dayStartMs = getLocalDayKeyMs(ms, language);

    if (!Number.isFinite(dayStartMs)) continue;

    const cur = byDay.get(dayStartMs) || {
      sum: 0,
      n: 0,
      min: value,
      max: value,
    };

    cur.sum += value;
    cur.n += 1;
    cur.min = Math.min(cur.min, value);
    cur.max = Math.max(cur.max, value);

    byDay.set(dayStartMs, cur);
  }

  const days = Array.from(byDay.entries())
    .map(([dayStartMs, agg]) => ({ dayStartMs, ...agg }))
    .sort((a, b) => a.dayStartMs - b.dayStartMs);

  const xValues = days.map((d) => d.dayStartMs);

  return {
    labels: xValues.map((ms) => formatDayMonth(ms, language)),
    xValues,
    values: days.map((d) => d.sum / d.n),
    minMax: days.map((d) => ({ min: d.min, max: d.max })),
    counts: days.map((d) => d.n),
  };
}
